import {
  Fade,
  ListItemIcon,
  ListItemText,
  makeStyles,
  MenuItem,
  MenuList,
  Popover,
} from '@material-ui/core';

import { isComposerToolbarDisabled } from '@src/selectors/agentConsole';
import { RootState } from '@src/store/reduxstore';
import React, { RefObject, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import PlusButton from './PlusButton';
import {
  AttachFileIcon,
  CropOriginalIcon,
  FlashIcon,
  MicNoneOutlinedIcon,
} from './SvgIcons';

interface IMoreOptions {
  searchQuickMessages: () => void;
  imageFileInputRef: RefObject<HTMLInputElement>;
  documentFileInputRef: RefObject<HTMLInputElement>;
  startAudioRecording: () => void;
}

const useStyles = makeStyles(theme => ({
  popover: {
    '& .MuiPopover-paper': {
      color: theme.palette.text.primary,
      gap: '16px',
      borderRadius: '16px',
      fontSize: '14px',
      fontFamily: 'Poppins',
      fontWeight: 400,
      lineHeight: '19.6px',
      textAlign: 'left',
      boxShadow: '0px 0px 8px 0px #00000026',
      maxWidth: '213px',
    },
    '& .MuiMenuItem-root': {
      borderRadius: '0',
      '&:hover': {
        color: theme.palette.primary.main,
        backgroundColor: `${theme.b2chatblue[100]} !important`,
        '& .MuiListItemIcon-root': {
          color: theme.palette.primary.main,
        },
        '& .MuiListItemText-primary': {
          fontWeight: 600,
        },
      },
    },
    '&.MuiListItem-button': {
      border: 0,
    },
    '& .MuiListItemIcon-root': {
      padding: '0 0 0 4px',
    },
  },
}));

export const MoreOptions: React.FC<IMoreOptions> = ({
  searchQuickMessages,
  startAudioRecording,
  imageFileInputRef,
  documentFileInputRef,
}) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { formatMessage } = useIntl();
  const { popover } = useStyles();

  const isFileSubmissionEnabled = useSelector(
    (state: RootState) => state.generalProperties.isFileSubmissionEnabled
  );
  const composerToolBarDisabled = useSelector(isComposerToolbarDisabled);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(!open);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const MENU_OPTIONS = [
    {
      icon: FlashIcon,
      labelId: 'QuickMessages.title',
      action: () => searchQuickMessages(),
    },
    {
      icon: AttachFileIcon,
      labelId: 'ChatMessageComposer.moreOptions.menu.file.label',
      action: () => {
        documentFileInputRef.current?.click();
      },
      disabled: !isFileSubmissionEnabled,
    },
    {
      icon: CropOriginalIcon,
      labelId: 'ChatMessageComposer.moreOptions.menu.image.label',
      action: () => {
        imageFileInputRef.current?.click();
      },
      disabled: !isFileSubmissionEnabled,
    },
    {
      icon: MicNoneOutlinedIcon,
      labelId: 'ChatMessageComposer.moreOptions.menu.voiceMessage.label',
      action: () => {
        startAudioRecording();
      },
    },
  ];

  const MenuView = MENU_OPTIONS.map(option => ({
    ...option,
    label: formatMessage({ id: option.labelId }),
  }));

  return (
    <div>
      <PlusButton
        onClick={handleClick}
        open={open}
        disabled={composerToolBarDisabled}
      />
      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        elevation={0}
        TransitionComponent={Fade}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        className={popover}
      >
        <MenuList>
          {MenuView.map(({ label, icon, action, disabled }) => (
            <MenuItem
              key={label}
              onClick={() => {
                if (action) action();
                setTimeout(() => {
                  handleClose();
                }, 300);
              }}
              disabled={disabled}
            >
              <ListItemIcon>{icon}</ListItemIcon>
              <ListItemText primary={label} />
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
    </div>
  );
};

export default MoreOptions;
